// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// Licensed under the MIT License — see LICENSE file in the repo root.
// SPDX-License-Identifier: MIT

"use client";

import { useEffect } from "react";

import { ToastAction, useToast } from "@tracepro/ui";
import { withBase } from "@tracepro/mobile-host/base-path";

/**
 * Đăng ký sw.js cho PWA — CHỈ ở bản dựng production.
 *
 * Vì sao phải chừa dev ra: sw.js phục vụ `/_next/static/` theo lối cache-first,
 * và tên kho cache là `tracepro-web-__CACHE_VERSION__`. Chuỗi `__CACHE_VERSION__`
 * chỉ được thay bằng dấu thời gian TRONG Dockerfile (`sed` ở bước build). Chạy
 * `next dev` ở máy thì nó nằm nguyên là chuỗi literal, nên:
 *
 *   · tên kho KHÔNG bao giờ đổi → nhánh `activate` không xoá kho cũ bao giờ
 *   · chunk JS đã cache được phục vụ VĨNH VIỄN, không hỏi lại máy chủ
 *   · Cmd+Shift+R cũng không cứu được — service worker chặn ngay tầng fetch
 *
 * Hậu quả thực tế (14/08/2026): sửa code xong, dev server phục vụ bản mới, mà
 * trình duyệt vẫn hiện bản cũ. Mất hai lần truy tìm nhầm hướng — một lần tưởng
 * mục menu bị ẩn theo quyền, một lần tưởng nút chưa được thêm.
 *
 * Bản production vẫn giữ nguyên hành vi cũ: Docker thay __CACHE_VERSION__ bằng
 * dấu thời gian nên mỗi lần dựng ảnh là một kho mới, kho cũ bị activate dọn.
 *
 * GỠ service worker cũ khi chạy dev: người đã dính SW từ trước phải được thoát
 * ra, không thì họ kẹt mãi dù bản vá này đã có.
 */
export function ServiceWorkerRegistrar() {
  const { toast } = useToast();

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    if (process.env.NODE_ENV !== "production") {
      void (async () => {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(
              keys.filter((k) => k.startsWith("tracepro-web-")).map((k) => caches.delete(k)),
            );
          }
        } catch {
          /* dọn dẹp là best-effort — hỏng cũng không được chặn app khởi động */
        }
      })();
      return;
    }

    navigator.serviceWorker.register(withBase("/sw.js")).catch(() => {
      // SW registration failed — likely not served over HTTPS.
    });

    // Bản mới: Docker đóng dấu thời gian vào sw.js nên mỗi lần deploy là một SW mới;
    // sw.js tự skipWaiting + claim → `controllerchange` báo trang đang chạy JS cũ.
    // Lần cài đầu (chưa có controller) không phải bản mới nên bỏ qua.
    const hadController = !!navigator.serviceWorker.controller;
    const onUpdated = () => {
      if (!hadController) return;
      toast({
        title: "Có phiên bản mới",
        description: "Tải lại trang để dùng bản mới nhất. Dữ liệu đang nhập nên được lưu trước.",
        duration: 60_000,
        action: <ToastAction altText="Tải lại trang" onClick={() => window.location.reload()}>Tải lại</ToastAction>,
      });
    };
    navigator.serviceWorker.addEventListener("controllerchange", onUpdated);
    // Trình duyệt chỉ tự hỏi sw.js khi điều hướng; tab mở lâu thì hỏi lại khi quay về.
    const checkUpdate = () => {
      if (document.visibilityState === "visible") void navigator.serviceWorker.getRegistration().then((r) => r?.update()).catch(() => {});
    };
    document.addEventListener("visibilitychange", checkUpdate);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onUpdated);
      document.removeEventListener("visibilitychange", checkUpdate);
    };
  }, [toast]);

  useEffect(() => {
    // B-03: nghe tin từ hàng đợi offline. Không có chỗ nghe thì mọi xử lý bên
    // service worker vẫn là "im lặng" với người dùng — đúng cái lỗi đang chữa.
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; pending?: number; status?: number; sent?: number } | null;
      if (!d || typeof d.type !== "string") return;
      if (d.type === "offline-event-held") {
        toast({
          title: "Bản ghi vẫn được giữ trên máy",
          description: d.status === 401 || d.status === 403
            ? "Đăng nhập hoặc xác nhận quy trình rồi gửi lại. Nội dung của bạn chưa bị xoá."
            : "Máy chủ chưa nhận bản ghi. Kiểm tra dữ liệu và trạng thái lô trước khi gửi lại.",
          variant: "destructive",
          duration: 10000,
        });
      } else if (d.type === "offline-queue-needs-auth") {
        toast({
          title: "Cần đăng nhập để gửi nhật ký đã lưu",
          description: `Còn ${d.pending ?? 0} bản ghi chờ gửi. Chúng vẫn được giữ nguyên — đăng nhập lại rồi hệ thống tự gửi tiếp.`,
          variant: "destructive",
          duration: 10000,
        });
      } else if (d.type === "offline-queue-rejected") {
        toast({
          title: "Một bản ghi offline bị máy chủ từ chối",
          description: `Máy chủ trả mã ${d.status ?? "?"}. Bản ghi vẫn được giữ trên máy để bạn kiểm tra và gửi lại.`,
          variant: "destructive",
          duration: 12000,
        });
      } else if (d.type === "offline-queue-sent" && (d.sent ?? 0) > 0) {
        toast({
          title: "Đã gửi xong nhật ký lưu offline",
          description: `${d.sent} bản ghi đã lên máy chủ.`,
        });
      }
    };
    // SEC-R2-11: phát lại nay do TRANG làm (lib/sw-queue-replay.ts) và báo kết quả
    // qua window event "tp-offline-queue" cùng hình tin nhắn — cùng một chỗ hiện toast.
    const onLocal = (e: Event) => onMessage({ data: (e as CustomEvent).detail } as MessageEvent);
    if ("serviceWorker" in navigator) navigator.serviceWorker.addEventListener("message", onMessage);
    window.addEventListener("tp-offline-queue", onLocal);
    return () => {
      if ("serviceWorker" in navigator) navigator.serviceWorker.removeEventListener("message", onMessage);
      window.removeEventListener("tp-offline-queue", onLocal);
    };
  }, [toast]);

  return null;
}
